import type { Namespace } from '../types';

// Shared by the Sensors and Actuators device screens (R-225 / D-136) — one
// namespace rather than duplicated keys in two catalogs, because it is one panel.
//
// DE drafted, queued for native review like the other catalogs.
export const calibration = {
  notStated: { de: 'Nicht angegeben', en: 'Not stated' },
  unknownModel: { de: 'unbekanntes Modell', en: 'unknown model' },
  saveFailed: { de: 'Speichern fehlgeschlagen', en: 'Could not save' },

  // ── resolution / identity ──────────────────────────────────────────────────
  noDevice: {
    de: 'Diesem Ort ist kein Controller zugewiesen, daher gibt es keine Sonde zu kalibrieren. Ordne im Mapper ein Board zu.',
    en: 'No controller is assigned to this location, so there is no probe to calibrate. Assign a board in the Mapper first.',
  },
  uncalibrated: { de: 'Nicht kalibriert', en: 'Not calibrated' },

  // ── facts ─────────────────────────────────────────────────────────────────
  liveReading: { de: 'Aktueller Messwert', en: 'Live reading' },
  // The pair is the point: what the probe measured, beside what we report.
  asMeasured: { de: 'Roh gemessen', en: 'As measured' },
  operatingRange: { de: 'Messbereich', en: 'Operating range' },
  recordedCorrection: { de: 'Erfasste Korrektur', en: 'Recorded correction' },
  inForceSince: { de: 'Gültig seit', en: 'In force since' },

  // ── what the bridge is actually doing ─────────────────────────────────────
  // "We recorded v7" and "the bridge is applying v7" are different facts. A
  // bridge holding no document publishes readings that are simply uncorrected —
  // plausible-looking and wrong — so this is stated, never assumed.
  bridgeApplying: { de: 'Bridge wendet an', en: 'Bridge is applying' },
  bridgeSilent: {
    de: 'Diese Bridge hat nichts gemeldet — unbekannt, ob Korrekturen angewendet werden',
    en: 'This bridge has reported nothing — unknown whether any correction is being applied',
  },
  bridgeNoDocument: {
    de: 'Bridge hat keine Kalibrierung erhalten — Messwerte sind unkorrigiert',
    en: 'Bridge has received no calibration — readings are uncorrected',
  },
  // `_one`/`_many` pair — the convention in this catalog set (see iot-control);
  // there is no ICU machinery, so the caller picks. "1 channels corrected" was the
  // wart the hardware E2E run surfaced.
  bridgeChannels_one: { de: '{n} Kanal korrigiert', en: '{n} channel corrected' },
  bridgeChannels_many: { de: '{n} Kanäle korrigiert', en: '{n} channels corrected' },
  bridgeNeverSees: {
    de: 'Bridge sieht diesen Kanal nie — die Korrektur wirkt nicht',
    en: 'Bridge never sees this channel — the correction is doing nothing',
  },

  // ── the firmware's own verdict ────────────────────────────────────────────
  unhealthyProbe: {
    de: 'Das Board meldet diese Sonde ({model}) als fehlerhaft. Eine Kalibrierung behebt keinen Defekt.',
    en: 'The board reports this probe ({model}) as faulty. Calibrating will not fix a fault.',
  },

  // ── the editor ────────────────────────────────────────────────────────────
  offsetTitle: { de: 'Gegen Referenz korrigieren', en: 'Correct against a reference' },
  twoPointTitle: { de: 'Zweipunkt-Kalibrierung', en: 'Two-point calibration' },
  twoPointHelp: {
    de: 'Sonde in beide Pufferlösungen tauchen und ablesen. Faktor und Offset werden daraus berechnet und mitgespeichert.',
    en: 'Put the probe in each buffer solution and read it. Scale and offset are derived from these and stored alongside them.',
  },

  // One signed number, entered as a size plus a direction (backend D-168). The
  // multiplier this class used to offer is gone: it is only honest when derived
  // from measured references, and hand-typed it slips past both fat-finger guards.
  correction: { de: 'Korrektur ({unit})', en: 'Correction ({unit})' },
  correctionHint: { de: 'Richtung wählen, Betrag eintragen', en: 'Pick the side, type the size' },
  correctionHelp: {
    de: 'Diese Zahl wird auf jeden Messwert addiert. Liest die Sonde zu NIEDRIG, wählen Sie +; liest sie zu hoch, wählen Sie −. Nichts ist vorausgewählt: die falsche Richtung halbiert den Fehler nicht, sie verdoppelt ihn.',
    en: 'This number is added to every reading. If the probe reads LOW, choose +; if it reads high, choose −. Nothing is preselected: the wrong direction does not halve the error, it doubles it.',
  },
  signPlus: { de: '+ addieren', en: '+ add' },
  signMinus: { de: '− abziehen', en: '− subtract' },

  refLow: { de: 'Untere Referenz ({unit})', en: 'Low reference ({unit})' },
  refLowHelp: {
    de: 'Der bekannte Wert der unteren Pufferlösung, z. B. pH 4,00.',
    en: 'The known value of the low buffer solution — pH 4.00, for example.',
  },
  readLow: { de: 'Anzeige darin ({unit})', en: 'Probe read ({unit})' },
  readLowHelp: {
    de: 'Was die Sonde in dieser Lösung tatsächlich angezeigt hat.',
    en: 'What the probe actually showed in that solution.',
  },
  refHigh: { de: 'Obere Referenz ({unit})', en: 'High reference ({unit})' },
  readHigh: { de: 'Anzeige darin ({unit})', en: 'Probe read ({unit})' },

  // ── the flow meter: measured, never a typed multiplier (backend R-332) ─────
  ratioTitle: { de: 'Gegen ein gemessenes Volumen kalibrieren', en: 'Calibrate against a measured volume' },
  ratioHelp: {
    de: 'Ein bekanntes Volumen durchlaufen lassen und beide Werte eintragen. Der Faktor wird daraus berechnet — der Impuls-pro-Liter-Wert des Zählers steckt fest in der Firmware, deshalb ist das die einzige Korrektur.',
    en: "Run a known volume through the meter and enter both figures. The scale is derived from them — the meter's pulses-per-litre is fixed in firmware, so this is the only correction there is.",
  },
  ratioTrue: { de: 'Gemessenes Volumen (L)', en: 'Volume you measured (L)' },
  ratioTrueHelp: {
    de: 'Wie viel wirklich durchgelaufen ist — im Messbecher abgelesen.',
    en: 'How much actually went through, read off a measuring jug.',
  },
  ratioIndicated: { de: 'Angezeigtes Volumen (L)', en: 'Volume it indicated (L)' },
  ratioIndicatedHelp: {
    de: 'Was der Zähler über denselben Durchlauf gemeldet hat.',
    en: 'What the meter reported over that same run.',
  },

  // ══ pump throughput (backend R-337) ════════════════════════════════════════
  // ⚠ NOT a calibration: `device_calibrations` holds a correction applied to a
  // reading, this holds a property of a machine. Same screen, same technician,
  // different fact — the copy says so rather than letting the shared page imply
  // they are the same thing.
  pumpTitle: { de: 'Pumpenleistung', en: 'Pump throughput' },
  pumpSectionSubtitle: {
    de: 'Keine Korrektur, sondern eine Eigenschaft der Maschine: was die Pumpe tatsächlich fördert. Nichts wendet diese Zahl auf einen Messwert an.',
    en: 'Not a correction but a property of the machine: what the pump actually moves. Nothing applies this number to a reading.',
  },
  colPump: { de: 'Pumpe', en: 'Pump' },
  colSetBy: { de: 'Gesetzt von', en: 'Set by' },
  setByDevice: { de: 'Am Gerät', en: 'On the device' },
  setBySite: { de: 'Hier gemessen', en: 'Measured here' },
  pumpName_water_pump: { de: 'Wasserpumpe', en: 'Water Pump' },
  pumpName_dosing_pump_a: { de: 'Dosierpumpe A', en: 'Dosing Pump A' },
  pumpName_dosing_pump_b: { de: 'Dosierpumpe B', en: 'Dosing Pump B' },
  pumpName_dosing_pump_c: { de: 'Dosierpumpe C', en: 'Dosing Pump C' },
  pumpName_dosing_pump_d: { de: 'Dosierpumpe D', en: 'Dosing Pump D' },

  // A dosing pump's figure belongs to its BOARD. Shown, never editable — a second
  // copy here would be two numbers for one pump, free to disagree.
  pumpDeviceIntro: {
    de: 'Diese Pumpe kennt ihre eigene Förderleistung. Sie steht im NVS der Platine, die Firmware rechnet damit jede Dosierung in eine Laufzeit um, und die Bridge meldet sie herauf. Kalibriert wird sie unten — der Wert wird dabei AUF DER PLATINE gespeichert, nie hier als zweite Zahl, die ihr widersprechen könnte.',
    en: "This pump knows its own delivery rate. It lives in the board's NVS, the firmware uses it to turn every dose into a run time, and the bridge reports it back up. Calibrate it below — the figure is stored ON THE BOARD, never here as a second number free to disagree with it.",
  },
  pumpDeviceRate: { de: 'Von der Platine gemeldet', en: 'Reported by the board' },
  pumpDoseMeans: { de: 'Damit dauern {ml} mL', en: 'So a {ml} mL dose takes' },
  pumpReportedAs: { de: 'Meldung im Wortlaut', en: 'Reported as' },
  // Running `CAL`/`CALSET` from the console (backend R-337). ⚠ This RUNS THE PUMP,
  // so it is gated on actuator control rather than on farming, and the copy says
  // what is about to physically happen before it happens.
  pumpCalTitle: { de: 'Pumpe neu kalibrieren', en: 'Recalibrate this pump' },
  pumpCalHelp: {
    de: '⚠ Die Pumpe läuft dabei WIRKLICH. Erst einen Behälter unterstellen, dann laufen lassen, wiegen und das Ergebnis eintragen — die Platine rechnet daraus selbst mL/s und speichert es im NVS.',
    en: '⚠ This RUNS THE PUMP. Put a container under the outlet first, run it, weigh what comes out and enter that — the board works out its own mL/s from the pair and stores it in NVS.',
  },
  pumpCalRun: { de: '1 · Laufzeit (Sekunden)', en: '1 · Run for (seconds)' },
  pumpCalRunHint: { de: '10–120 s', en: '10–120 s' },
  pumpCalRunHelp: {
    de: 'Die Platine teilt die aufgefangene Menge durch diese Zeit. Unter 10 s entscheidet der Wiegefehler das Ergebnis; über 120 s bricht die Firmware den Lauf ab (MAX_DOSE_MS), während Ihre gemessene Menge die ganze Zeit annimmt — das ergibt ein falsches mL/s, das wie ein Erfolg aussieht.',
    en: 'The board divides the caught volume by this time. Under 10 s the weighing error decides the result; over 120 s the firmware cuts the run short (MAX_DOSE_MS) while your measured volume assumed all of it, which computes a wrong mL/s that looks like a success.',
  },
  pumpCalRunAction: { de: 'Pumpe starten', en: 'Run the pump' },
  pumpCalRunning: { de: 'Pumpe läuft — auffangen und wiegen', en: 'Pump is running — catch it and weigh it' },
  pumpCalMeasured: { de: '2 · Aufgefangen (mL)', en: '2 · Collected (mL)' },
  pumpCalMeasuredHint: { de: 'Wiegen: 1 g Wasser = 1 mL', en: 'Weigh it: 1 g of water is 1 mL' },
  pumpCalMeasuredHelp: {
    de: 'Erscheint erst nach einem Lauf, weil die Platine eine Menge ohne vorherigen Lauf ablehnt ("run CAL first") — es gäbe nichts, wodurch geteilt werden könnte.',
    en: 'Only appears after a run, because the board refuses a volume with no run behind it ("run CAL first") — there would be nothing to divide it by.',
  },
  pumpCalStoreAction: { de: 'Auf der Platine speichern', en: 'Store on the board' },
  pumpCalWouldStore: { de: 'Die Platine speichert: {rate}', en: 'The board will store: {rate}' },
  pumpCalStored: {
    de: 'Gespeichert. Der neue Wert erscheint, sobald die Platine ihn erneut meldet.',
    en: 'Stored. The new figure appears once the board announces it again.',
  },
  pumpCalAfter: {
    de: 'Der Wert lebt im NVS der Platine, nicht hier — er überlebt einen Neustart und wird von der Bridge zurückgemeldet.',
    en: "The figure lives in the board's NVS, not here — it survives a reboot and is reported back by the bridge.",
  },
  pumpCalNeedControl: {
    de: 'Zum Kalibrieren wird Schreibrecht auf der Aktorsteuerung benötigt — es lässt eine Pumpe laufen.',
    en: 'Calibrating needs Write on actuator control — it runs a pump.',
  },
  pumpCalNoRoom: {
    de: 'Diese Platine ist keinem Plumbing-Raum zugeordnet, daher kann der Befehl nicht adressiert werden. Zuerst im Mapper zuordnen.',
    en: 'This board is not mapped to a plumbing room, so the command cannot be addressed. Map it first.',
  },
  pumpDeviceRecalibrate: {
    de: 'Neu kalibrieren heißt: Pumpe laufen lassen, Ausbringung wiegen, Ergebnis speichern. Das geht jetzt von hier aus — unten.',
    en: 'Recalibrating means running the pump, weighing what comes out and storing the result. That can now be done from here — below.',
  },
  pumpDeviceHowToChange: {
    de: 'Neu kalibrieren heißt: am Gerät `CAL` laufen lassen, die Ausbringung wiegen und mit `CALSET` speichern. Aus dieser Konsole ist das noch nicht möglich — der Einmal-Befehlspfad ist im Vertrag reserviert, aber von der Bridge noch nicht umgesetzt.',
    en: 'Recalibrating means running `CAL` on the device, weighing what comes out and storing it with `CALSET`. That cannot be driven from this console yet — the one-shot command path is reserved in the contract but not yet implemented by the bridge.',
  },
  pumpDeviceUncalibrated: {
    de: 'Diese Platine meldet keine Förderleistung. Bis sie kalibriert ist, dosiert die Firmware mit ihrem Standardwert von 1,0 mL/s — eine Vorgabe, keine Messung.',
    en: 'This board reports no delivery rate. Until it is calibrated the firmware doses with its built-in default of 1.0 mL/s, which is a guess rather than a measurement.',
  },
  pumpName_solenoid_valve: { de: 'Magnetventil', en: 'Solenoid Valve' },
  pumpName_solenoid_zone: { de: 'Zonenventil', en: 'Zone Valve' },
  pumpUnmeasured: { de: 'Nicht gemessen', en: 'Not measured' },
  pumpUnmeasuredIntro: {
    de: 'Wie viel diese Pumpe wirklich fördert, wurde nie gemessen — bei Ausfall des Durchflusszählers läuft sie nach der Zeit aus dem Kulturrezept, nicht nach ihrer tatsächlichen Leistung.',
    en: 'How much this pump really moves has never been measured. If the flow meter goes quiet, an irrigation window falls back to a time from the crop recipe rather than to what this pump actually does.',
  },
  pumpMeasuredIntro: {
    de: 'Gemessen, nicht angenommen. Die Leistung ändert sich mit Förderhöhe, Filterzustand und geöffnetem Zonenventil — die Notiz hält fest, unter welchen Bedingungen gemessen wurde.',
    en: 'Measured, not assumed. Throughput moves with head pressure, filter state and which zone valve was open — the note records the conditions it was taken under.',
  },
  pumpRate: { de: 'Gemessene Leistung', en: 'Measured throughput' },
  pumpFrom: { de: 'Aus dieser Füllung', en: 'From this fill' },
  pumpFromValue: { de: '{litres} L in {seconds} s', en: '{litres} L in {seconds} s' },
  pumpMeans: { de: 'Damit dauern {litres} L', en: 'So {litres} L takes' },
  pumpMeansValue: { de: '{seconds} s', en: '{seconds} s' },
  pumpSince: { de: 'Gültig seit', en: 'In force since' },

  pumpMeasureTitle: { de: 'Füllung messen', en: 'Measure a fill' },
  pumpMeasureHelp: {
    de: 'Pumpe einschalten, in einen Behälter fördern, ausschalten und die Zeit stoppen. Beide Werte eintragen — die Leistung wird daraus berechnet, damit sie der Messung nicht widersprechen kann.',
    en: 'Switch the pump on, fill a container, switch it off and time it. Enter both figures — the rate is worked out from them, so it cannot disagree with the measurement behind it.',
  },
  pumpVolume: { de: 'Aufgefangene Menge', en: 'How much you collected' },
  pumpVolumeHint: { de: 'Wiegen oder im Messbecher ablesen', en: 'Weigh it, or read a measuring jug' },
  pumpVolumeHelp: {
    de: 'Eine Waage ist am genauesten: 1 kg Wasser = 1 L (bis 30 °C auf 0,3 % genau). Keine Gallonen — US- und imperiale Gallone unterscheiden sich um 17 %, und auf einem Kanister steht selten welche.',
    en: 'A scale is the most accurate tool most sites have: 1 kg of water is 1 litre, true to 0.3 % up to 30 °C. No gallons offered — a US and an imperial gallon differ by 17 % and a container rarely says which.',
  },
  pumpTime: { de: 'Wie lange es dauerte', en: 'How long it took' },
  pumpTimeHint: { de: 'Mindestens 10 Sekunden', en: 'At least 10 seconds' },
  pumpTimeHelp: {
    de: 'Eine von Hand gestartete und gestoppte Messung kostet an jedem Ende rund eine Sekunde Reaktionszeit. Bei einer kurzen Füllung misst man deshalb eher sich selbst als die Pumpe — länger ist immer besser.',
    en: 'Starting and stopping a timer by hand costs about a second of reaction time at each end, so a short fill measures you rather than the pump. Longer is always better.',
  },
  pumpNote: { de: 'Notiz', en: 'Note' },
  pumpNoteHint: {
    de: 'Unter welchen Bedingungen — welches Zonenventil offen war, Filterzustand. Eine Leistung ohne diese Angaben ist eine Anekdote.',
    en: 'The conditions — which zone valve was open, the state of the filter. A rate with no record of those is an anecdote.',
  },
  pumpRecord: { de: 'Leistung erfassen', en: 'Record throughput' },
  pumpWouldRecord: { de: 'Ergibt: {rate}', en: 'Works out to: {rate}' },
  pumpRecorded: { de: 'Pumpenleistung erfasst', en: 'Pump throughput recorded' },
  pumpAppends: {
    de: 'Erfassen hängt an und überschreibt nie — eine Zahl vom Juni erklärt weiterhin einen Lauf im Juni.',
    en: 'Recording appends and never overwrites — a figure from June still explains a run from June.',
  },

  pumpWithdrawTitle: { de: 'Zahl zurückziehen', en: 'Withdraw this figure' },
  pumpWithdrawNote: { de: 'Warum sie nicht mehr gilt', en: 'Why it no longer stands' },
  pumpWithdrawHint: {
    de: 'Pflichtangabe — ein Rückzug ohne Grund sagt dem Nächsten nichts.',
    en: 'Required — a withdrawal with no reason tells the next person nothing.',
  },
  pumpWithdraw: { de: 'Zurückziehen', en: 'Withdraw' },
  pumpWithdrawn: {
    de: 'Zahl zurückgezogen — die Pumpe gilt wieder als ungemessen',
    en: 'Withdrawn — this pump counts as unmeasured again',
  },
  pumpWithdrawnEntry: { de: 'Zurückgezogen', en: 'Withdrawn' },
  pumpHistoryTitle: { de: 'Messverlauf', en: 'Measurement history' },
  pumpNoHistory: { de: 'Diese Pumpe wurde noch nie gemessen.', en: 'This pump has never been measured.' },

  unit_L: { de: 'Liter', en: 'litres' },
  unit_mL: { de: 'Milliliter', en: 'millilitres' },
  unit_kg: { de: 'Kilogramm', en: 'kilograms' },
  unit_s: { de: 'Sekunden', en: 'seconds' },
  unit_min: { de: 'Minuten', en: 'minutes' },

  // ── stopping a correction (backend R-332) ─────────────────────────────────
  revokeTitle: { de: 'Korrektur zurückziehen', en: 'Stop correcting this probe' },
  revokeHelp: {
    de: 'Die Sonde meldet danach wieder genau, was sie misst. Der Verlauf bleibt erhalten — Zurückziehen fügt einen Eintrag hinzu und löscht nichts.',
    en: 'The probe goes back to reporting exactly what it measures. The trail is kept — withdrawing appends an entry and deletes nothing.',
  },
  revokeReason: { de: 'Grund (erforderlich)', en: 'Reason (required)' },
  revokeReasonHint: {
    de: 'Eine zurückgezogene Korrektur enthält keine Zahlen — der Grund IST der Eintrag',
    en: 'A withdrawn correction carries no numbers — the reason IS the record',
  },
  revoke: { de: 'Korrektur zurückziehen', en: 'Stop correcting' },
  revoked: {
    de: 'Korrektur zurückgezogen — diese Sonde meldet jetzt genau, was sie misst',
    en: 'Correction withdrawn — this probe now reports exactly what it measures',
  },
  revokedEntry: { de: 'Zurückgezogen — wieder Rohwert', en: 'Withdrawn — back to raw' },

  note: { de: 'Notiz', en: 'Note' },
  noteHint: {
    de: 'Womit gemessen wurde — eine Kalibrierung ohne Herkunft ist eine Zahl, der niemand trauen sollte',
    en: 'What it was measured against — a calibration nobody can trace is a number nobody should trust',
  },

  record: { de: 'Kalibrierung erfassen', en: 'Record calibration' },
  recorded: { de: 'Kalibrierung erfasst', en: 'Calibration recorded' },
  wouldApply: { de: 'Würde anwenden: {correction}', en: 'Would apply: {correction}' },
  needWrite: {
    de: 'Kalibrieren erfordert Schreibrechte für Farming',
    en: 'Recording a calibration needs Write on farming',
  },
  appendOnly: {
    de: 'Erfassen fügt hinzu und überschreibt nie: alte Messwerte behalten die Korrektur, die damals galt.',
    en: 'Recording appends and never overwrites — past readings keep the correction that applied when they were taken.',
  },

  // ── what is deliberately not editable here ────────────────────────────────
  actuatorNotHere: {
    de: 'Aktoren werden am Gerät kalibriert (Dosierpumpen-mL/s, EZO-Sonden), nicht hier. Diese Aktion erfordert physischen Zugriff und läuft über das Kalibrier-Werkzeug der Anlage.',
    en: 'Actuators are calibrated on the device itself (dosing-pump mL/s, EZO probes), not here. That action needs physical access and goes through the site calibration tool.',
  },

  // ── audit trail ───────────────────────────────────────────────────────────
  historyTitle: { de: 'Kalibrierverlauf', en: 'Calibration history' },
  noHistory: {
    de: 'Diese Sonde wurde noch nie kalibriert — Messwerte werden unverändert veröffentlicht.',
    en: 'This probe has never been calibrated — its readings are published exactly as measured.',
  },
  derivedFrom: {
    de: 'aus {refLow}→{readLow}, {refHigh}→{readHigh}',
    en: 'from {refLow}→{readLow}, {refHigh}→{readHigh}',
  },

  // ══ the dedicated Calibration page (backend R-332) ═════════════════════════
  pageTitle: { de: 'Kalibrierung', en: 'Calibration' },
  pageDesc: {
    de: 'Jeder Sensorkanal, den ein Board gemeldet hat — mit seiner erfassten Korrektur und damit, was die Bridge tatsächlich anwendet. Beides ist nicht dasselbe.',
    en: 'Every sensor channel a board has announced, with its recorded correction and what the bridge reports it is actually applying. Those are not the same fact.',
  },

  statChannels: { de: 'Sensorkanäle', en: 'Sensor channels' },
  // "Calibrated" is the REGISTER's answer (a correction is recorded, whatever its
  // delivery state); "Applied" is one of nine DELIVERY states and a strict subset.
  // The two are different questions and the tiles must not blur them.
  statCalibrated: { de: 'Kalibriert', en: 'Calibrated' },
  statUncalibrated: { de: 'Nicht kalibriert', en: 'Not calibrated' },
  statAttention: { de: 'Prüfen', en: 'Need attention' },
  statNotHere: { de: 'Nicht hier kalibriert', en: 'Not calibrated here' },

  attentionBanner: {
    de: '{n} Sonde(n) brauchen Aufmerksamkeit: eine Korrektur, die die Daten nicht erreicht, oder ein Kanal, den der Katalog nicht kennt. Zeile öffnen, um zu sehen, warum.',
    en: '{n} probe(s) need attention: a correction that is not reaching the data, or a channel the catalogue does not know. Open a row to see why.',
  },

  // The Correction column's filter — the axis the Status column cannot answer.
  // Named "Recorded"/"None" rather than reusing "Not calibrated", which is already
  // a STATUS option meaning something narrower.
  filterRecorded: { de: 'Erfasst', en: 'Recorded' },
  filterNone: { de: 'Keine', en: 'None' },

  colProbe: { de: 'Sonde', en: 'Probe' },
  colBoard: { de: 'Board', en: 'Board' },
  colPlace: { de: 'Ort', en: 'Place' },
  colMethod: { de: 'Verfahren', en: 'Method' },
  colStatus: { de: 'Status', en: 'Status' },
  colCorrection: { de: 'Korrektur', en: 'Correction' },

  unmapped: { de: 'Nicht zugeordnet', en: 'Not mapped' },
  noModel: { de: 'kein Modell gemeldet', en: 'no model reported' },

  // Methods, in the operator's terms rather than the enum's.
  methodOffset: { de: 'Offset', en: 'Offset' },
  methodTwoPoint: { de: 'Zweipunkt', en: 'Two-point' },
  methodRatio: { de: 'Volumen', en: 'Volume' },
  methodLightChain: { de: 'Pro Raum', en: 'Per room' },
  methodBoolean: { de: 'Schalter', en: 'Switch' },

  // The nine statuses. Each says what is true, not just that something is wrong —
  // an operator has to know which of these to act on and how.
  statusApplied: { de: 'Angewendet', en: 'Applied' },
  statusAppliedWhy: {
    de: 'Erfasst, ausgeliefert und von der Bridge bestätigt.',
    en: 'Recorded, delivered, and confirmed by the bridge.',
  },
  statusUncalibrated: { de: 'Nicht kalibriert', en: 'Not calibrated' },
  statusUncalibratedWhy: {
    de: 'Keine Korrektur erfasst — die Sonde meldet genau, was sie misst.',
    en: 'No correction recorded — the probe reports exactly what it measures.',
  },
  statusPending: { de: 'Wird übernommen', en: 'Being picked up' },
  statusPendingWhy: {
    de: 'Erfasst; die Bridge hält noch ein älteres Dokument. Klärt sich in einem Publish-Zyklus (20 s).',
    en: 'Recorded; the bridge is still on an older document. Clears within one publish tick (20 s).',
  },
  statusUndeliverable: { de: 'Nicht auslieferbar', en: 'Cannot be delivered' },
  statusUndeliverableWhy: {
    de: 'Erfasst für ein Board, das keinem Eltern-Baum zugeordnet ist — keine Bridge erhält es je. Board im Mapper zuordnen.',
    en: 'Recorded against a board in no parent tree, so no bridge will ever receive it. Assign the board in the Mapper.',
  },
  statusUnknownToBridge: { de: 'Bridge sieht den Kanal nicht', en: 'Bridge never sees it' },
  statusUnknownToBridgeWhy: {
    de: 'Die Bridge hält die Korrektur, hat aber noch nie einen Messwert auf diesem Kanal gesehen — ersetztes Board oder Tippfehler. Sie tut also nichts.',
    en: 'The bridge holds the correction but has never seen a reading on that channel — a replaced board, or a typo. It is silently doing nothing.',
  },
  statusNoBridgeReport: { de: 'Bridge meldet nicht', en: 'Bridge not reporting' },
  statusNoBridgeReportWhy: {
    de: 'Die zuständige Bridge meldet nichts, also ist UNBEKANNT, ob die Korrektur wirkt — was nicht dasselbe ist wie zu wissen, dass sie es nicht tut.',
    en: 'The responsible bridge has not reported, so whether the correction applies is UNKNOWN — which is not the same as knowing it does not.',
  },
  statusBridgeHasNoDocument: { de: 'Bridge hat kein Dokument', en: 'Bridge holds no document' },
  statusBridgeHasNoDocumentWhy: {
    de: 'Die Bridge meldet, dass sie gar kein Kalibrierdokument hält. Ihre Messwerte sind unkorrigiert, obwohl das Register etwas anderes sagt.',
    en: 'The bridge reports that it holds no calibration document at all. Its readings are uncorrected while the register says otherwise.',
  },
  statusNotCalibratable: { de: 'Hier nicht kalibrierbar', en: 'Not calibrated here' },
  statusNotInCatalogue: { de: 'Nicht im Katalog', en: 'Not in the catalogue' },
  statusNotInCatalogueWhy: {
    de: 'Kein Katalogeintrag, also gibt es weder Einheit noch Messbereich — der Kanal kann nicht kalibriert und nicht bewertet werden.',
    en: 'No catalogue row, so there is no unit and no operating range — this channel can be neither calibrated nor judged.',
  },

  filterAll: { de: 'Alle', en: 'All' },

  // ── the filter bar (28 probes on the office rig, hundreds on a real farm) ──
  filterSearch: { de: 'Sonden suchen…', en: 'Search probes…' },
  filterDevice: { de: 'Gerät', en: 'Device' },
  filterChannel: { de: 'Kanal', en: 'Channel' },
  filterStatus: { de: 'Status', en: 'Status' },
  filterShowing: { de: '{n} von {m} Sonden', en: '{n} of {m} probes' },
  // Each select states what it filters, because three dropdowns all reading
  // "All" leave a sighted operator no way to tell them apart — the aria-label
  // only helps a screen reader.
  filterAllDevices: { de: 'Alle Geräte', en: 'All devices' },
  filterAllChannels: { de: 'Alle Kanäle', en: 'All channels' },
  filterAllStatuses: { de: 'Alle Status', en: 'All statuses' },
  // A filter matching nothing is NOT the same fact as a farm reporting nothing,
  // and an operator would act differently on each — so it gets its own message.
  noMatchTitle: { de: 'Keine Sonde passt zum Filter', en: 'No probe matches the filter' },
  noMatchMsg: {
    de: 'Alle gemeldeten Kanäle sind noch da — nur keiner passt zu dieser Auswahl. Filter zurücksetzen, um wieder alle zu sehen.',
    en: 'Every reported channel is still there; none matches this selection. Clear the filters to see them all again.',
  },
  emptyTitle: { de: 'Keine Sensorkanäle gemeldet', en: 'No sensor channels reported' },
  emptyMsg: {
    de: 'Diese Seite listet, was die Boards selbst angekündigt haben. Wenn sie leer ist, hat noch kein Board eine Inventarmeldung geschickt.',
    en: 'This page lists what the boards themselves announced. If it is empty, no board has sent an inventory announcement yet.',
  },
  loadError: { de: 'Kalibrierung konnte nicht geladen werden', en: 'Could not load calibration' },
  unhealthyShort: { de: 'Board meldet Fehler', en: 'Board reports a fault' },

  // ── R-177 / R-355: the per-ROOM light calibration ──────────────────────────
  // A different fact from everything above. The rows in this table are per-PROBE
  // corrections; this one is a per-ROOM conversion factor, which is exactly why
  // the light chain shows "per room" instead of an editable offset.
  lightAction: { de: 'Licht kalibrieren (PAR-Messgerät)', en: 'Calibrate light (PAR meter)' },
  lightTitle: { de: 'Lichtkalibrierung des Raums', en: "This room's light calibration" },
  lightHelp: {
    de: 'Licht einschalten, PAR-Messgerät neben die Sonde halten und den abgelesenen PPFD-Wert eintragen. Der Faktor ergibt sich aus diesem Wert geteilt durch den aktuellen Rohzählwert — er wird nie von Hand eingetippt.',
    en: 'Switch the lights on, hold the PAR meter beside the probe and enter the PPFD it reads. The factor is that reading divided by the current raw count — it is never typed by hand.',
  },
  lightRawNow: { de: 'Rohzählwert jetzt', en: 'Raw count now' },
  lightCurrentFactor: { de: 'Aktueller Faktor', en: 'Current factor' },
  lightFactorIs: { de: 'Kalibriert {factor}', en: 'Calibrated {factor}' },
  lightUncalibrated: { de: 'Nicht kalibriert', en: 'Not calibrated' },
  lightMeterLabel: { de: 'PAR-Messgerät (µmol/m²/s)', en: 'PAR meter (µmol/m²/s)' },
  lightMeterHint: { de: 'Am Messgerät abgelesen', en: 'As read off the meter' },
  lightMeterHelp: {
    de: 'Der Wert, den das PAR-Messgerät an derselben Stelle und im selben Moment anzeigt wie die Sonde ihren Rohzählwert meldet.',
    en: 'What the PAR meter shows at the same spot, at the same moment the probe reports the raw count above.',
  },
  lightMeterInvalid: {
    de: 'Daraus lässt sich kein Faktor bilden — der Messwert muss größer als null sein.',
    en: 'No factor can come from that — the reading has to be greater than zero.',
  },
  lightNoteHint: {
    de: 'Messgerät, Höhe über dem Bestand, Dimmstufe — eine Kalibrierung, die niemand nachvollziehen kann, ist eine Zahl, der niemand trauen sollte.',
    en: 'Meter, height above the canopy, dimming level — a calibration nobody can trace is a number nobody should trust.',
  },
  lightRecord: { de: 'Kalibrierung erfassen', en: 'Record calibration' },
  lightRecorded: { de: 'Lichtkalibrierung erfasst', en: 'Light calibration recorded' },
  lightWouldRecord: { de: 'Erfasst {factor} pro Rohzählwert', en: 'Records {factor} per raw count' },
  lightAppendOnly: {
    de: 'Erfassen hängt an: Der bisherige Faktor bleibt erhalten, damit ältere Messwerte mit dem Faktor lesbar bleiben, der damals galt.',
    en: 'Recording appends: the previous factor stays, so older readings remain readable with the factor that applied when they were taken.',
  },
  // Two different refusals, kept apart on purpose — one is a mapping problem, the
  // other means "switch the lights on".
  lightNoReading: {
    de: 'In diesem Raum meldet keine Sonde einen Licht-Rohzählwert. Ohne diesen Wert gibt es nichts zu teilen — erst die Zuordnung der Boards prüfen.',
    en: 'No probe in this room is reporting a raw light count. Without one there is nothing to divide by — check the board mapping first.',
  },
  lightDark: {
    de: 'Der Rohzählwert ist null: Die Sonde sieht kein Licht. Erst das Licht einschalten — ein dunkler Raum lässt sich nicht kalibrieren.',
    en: 'The raw count is zero: the probe sees no light. Switch the lights on first — a dark room cannot be calibrated.',
  },
} satisfies Namespace;
